import { swrFetcher } from "@/lib/api";
import { useAuthStore } from "@/store/auth-store";
import useCalendarStore from "@/store/calendar-store";
import { dateToLocalTime } from "@/utils/format-date-with-locale";
import { useMemo } from "react";
import useSWR from "swr";

// TODO optimize this with a special endpoint that returns only IDs from to
export function useUserBookings(from?: string, to?: string) {
  const { isAuthenticated } = useAuthStore();

  const { currentDate, view } = useCalendarStore();

  // Determine date range based on view
  const { startDate, endDate } = useMemo(() => {
    // Start with the current date
    const currentDateObj = new Date(currentDate);

    // Initialize startDate and endDate
    let startDate, endDate;

    if (view === "day") {
      // DAY VIEW: Use exact current date, set to beginning of day
      startDate = dateToLocalTime(new Date(currentDateObj));
      startDate.setHours(0, 0, 0, 0);

      // End date is same day at end of day
      endDate = dateToLocalTime(new Date(startDate));
      endDate.setHours(23, 59, 59, 999);
    } else if (view === "week") {
      // WEEK VIEW: Find Monday of current week
      startDate = dateToLocalTime(new Date(currentDateObj));
      const day = startDate.getDay(); // 0 is Sunday, 1 is Monday, ..., 6 is Saturday

      // Adjust to previous Monday (or stay on Monday if already Monday)
      const daysToSubtract = day === 0 ? 6 : day - 1;
      startDate.setDate(startDate.getDate() - daysToSubtract);
      startDate.setHours(0, 0, 0, 0);

      // End date is Sunday (6 days after Monday)
      endDate = dateToLocalTime(new Date(startDate));
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 0, 0, 0);
    } else if (view === "month") {
      // MONTH VIEW: First day of the current month
      startDate = dateToLocalTime(new Date(currentDateObj));
      startDate.setDate(1); // Set to 1st day of month
      startDate.setHours(0, 0, 0, 0);

      // Last day of the current month
      endDate = dateToLocalTime(new Date(startDate));
      // Move to first day of next month, then subtract one day
      endDate.setMonth(endDate.getMonth() + 1);
      endDate.setDate(0);
      endDate.setHours(23, 59, 59, 999);
    }

    return { startDate, endDate };
  }, [currentDate, view]);

  const params = new URLSearchParams();
  if (from) params.append("from", startDate.toISOString());
  if (to) params.append("to", endDate.toISOString());

  const key = isAuthenticated
    ? `/api/widget/profile/bookings/session-ids${params.toString() ? `?${params}` : ""}`
    : null;

  const { data, error, isLoading, mutate } = useSWR<string[]>(key, swrFetcher);

  const bookings = data ?? [];

  const hasBookedEvent = (classSessionId: string) => {
    return bookings?.some((b: string) => b === classSessionId);
  };

  return {
    bookings,
    hasBookedEvent,
    loading: isLoading,
    error,
    refresh: mutate,
  };
}
